import { FunctionComponent, useContext, useMemo, useState } from 'react'
import { Box, Paper, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { EditOutlined } from '@mui/icons-material'
import { NumericFormat } from 'react-number-format'
import { useNavigate } from 'react-router-dom'
import gaiaTheme from '../../../root-components/theme'
import { IReductionActionForReportPeriod, ReductionActionType } from '../../../shared/services/reduction-action/reduction-action.model'
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { ReductionActionPlanDialog } from '../../dialogs/reduction/reduction-action-plan-dialog'

type TimelineRow = {
  id: string
  name: string
  scope: string | null
  status: 'Completed' | 'In Progress' | 'Not Started'
  annualTonnes: number | null
  startYear: number
  endYear: number | null // null = ongoing (open-ended)
}

const STATUS_COLORS: Record<TimelineRow['status'], string> = {
  Completed: '#4c8c4a',
  'In Progress': '#5b8def',
  'Not Started': '#9e9e9e',
}

function resolveStatus(action: IReductionActionForReportPeriod): TimelineRow['status'] {
  const j = action.reportPeriodReductionAction
  if (j.isCompleted) return 'Completed'
  if (j.inProgress) return 'In Progress'
  return 'Not Started'
}

function buildRow(action: IReductionActionForReportPeriod): TimelineRow | null {
  const j = action.reportPeriodReductionAction
  // The configured start reporting period is the source of truth; reductionStartYear is only a
  // fallback (it can be stale after the start period changes), then the assigned reporting period.
  const start = j.reductionStartReportingPeriod?.calendarYear != null
    ? Number(j.reductionStartReportingPeriod.calendarYear)
    : (j.reductionStartYear != null
      ? Number(j.reductionStartYear)
      : (j.reportPeriod?.calendarYear != null ? Number(j.reportPeriod.calendarYear) : NaN))
  if (Number.isNaN(start)) return null
  const lifetime = j.reductionLifetimePeriods != null ? Number(j.reductionLifetimePeriods) : null
  const endYear = lifetime != null && lifetime > 0 ? start + lifetime - 1 : null
  const annual = j.annualReduction != null ? Number(j.annualReduction) / 1000 : null
  return {
    id: j.id,
    name: action.name,
    scope: action.group?.scope ?? null,
    status: resolveStatus(action),
    annualTonnes: annual,
    startYear: start,
    endYear,
  }
}

export const ReductionActionTimeline: FunctionComponent<{
  actions: IReductionActionForReportPeriod[]
  actionType: ReductionActionType
  onActionUpdated: () => void
  baseYear?: number | null
}> = ({ actions, actionType, onActionUpdated, baseYear }) => {
  const isMobile = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const labelWidth = isMobile ? 150 : 260
  const navigate = useNavigate()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reductionActionService = new ReductionActionService()
  const isEditable = actionType !== 'flag_removal'

  const [openDialog, setOpenDialog] = useState(false)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState(false)

  const rows = useMemo(
    () => actions.map(buildRow).filter((r): r is TimelineRow => r !== null),
    [actions]
  )

  const { years, hasOngoing } = useMemo(() => {
    if (rows.length === 0) return { years: [] as number[], hasOngoing: false }
    const starts = rows.map(r => r.startYear)
    const finiteEnds = rows.map(r => r.endYear).filter((e): e is number => e != null)
    const ongoing = rows.some(r => r.endYear == null)
    // Begin the axis at the plan's base year (so it's visible before actions start),
    // falling back to the earliest action start when no base year is provided.
    const minYear = baseYear != null && !Number.isNaN(baseYear)
      ? Math.min(baseYear, ...starts)
      : Math.min(...starts)
    let maxYear = Math.max(minYear, ...starts, ...finiteEnds)
    if (ongoing) maxYear += 2 // headroom so ongoing bars show their open-ended edge
    const list: number[] = []
    for (let y = minYear; y <= maxYear; y++) list.push(y)
    return { years: list, hasOngoing: ongoing }
  }, [rows, baseYear])

  const editClickHandler = (reportPeriodReductionActionId: string) => {
    if (!isEditable) return
    setOpenDialog(true)
    navigate(`/reduction-plan/actions?actionId=${reportPeriodReductionActionId}`)
  }

  const closeDialogHandler = () => {
    setOpenDialog(false)
    navigate('/reduction-plan/actions')
  }

  const submitDialogHandler = (reportPeriodReductionActionId: string, values: any) => {
    if (isDialogSubmitting) return
    setIsDialogSubmitting(true)
    reductionActionService.editReductionActionForReportPeriod(reportPeriodReductionActionId, values)
      .then(() => {
        onActionUpdated()
        setOpenDialog(false)
        setIsDialogSubmitting(false)
      })
      .catch((err) => {
        const message = err?.response?.data?.code === 1 ? 'Report period is locked' : 'There was an error, please try again'
        userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        setIsDialogSubmitting(false)
        console.log(err)
      })
  }

  const deleteDialogHandler = (reductionActionId: string, reportPeriodId: string) => {
    reductionActionService.deleteReductionActionToReportPeriod(reductionActionId, reportPeriodId)
      .then(() => {
        onActionUpdated()
        userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
        setOpenDialog(false)
      })
      .catch((err) => {
        const message = err?.response?.data?.code === 1 ? 'Report period is locked' : 'There was an error, please try again'
        userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        setOpenDialog(false)
        console.log(err)
      })
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          No actions with a start year to plot. Add a reduction action (with a start reporting period) to see it on the timeline.
        </Typography>
      </Box>
    )
  }

  const gridTemplate = `${labelWidth}px repeat(${years.length}, minmax(36px, 1fr))`

  return (
    <Box>
      <Paper variant='outlined' sx={{ mb: 2, maxWidth: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: labelWidth + years.length * 44 }}>
        {/* Header: label column + year axis (styled like the table header) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplate, alignItems: 'stretch', backgroundColor: 'lightGrey.light', borderRadius: '10px 10px 0 0', borderBottom: '1px solid', borderColor: 'lightGrey.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 2, py: 2 }}>
            <Typography variant='body2' sx={{ color: 'text.primary', fontWeight: 500 }}>
              {actionType === 'flag_removal' ? 'Removal' : 'Reduction Action'}
            </Typography>
          </Box>
          {years.map((year) => (
            <Typography key={year} variant='body2' sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'left', alignSelf: 'center', py: 2, pl: 1 }}>{year}</Typography>
          ))}
        </Box>

        {/* One row per action */}
        {rows.map((row) => {
          const startIdx = years.indexOf(row.startYear)
          const endIdx = row.endYear != null ? years.indexOf(row.endYear) : years.length - 1
          // grid columns are 1-based; column 1 is the label, year columns start at 2
          const colStart = 2 + Math.max(0, startIdx)
          const colEnd = 2 + Math.max(startIdx, endIdx) + 1
          const color = STATUS_COLORS[row.status]
          const rangeLabel = row.endYear != null ? `${row.startYear}–${row.endYear}` : `${row.startYear} onward`
          const tooltip =`${row.scope ? `Scope ${row.scope} · ` : ''}${row.status}\nActive: ${rangeLabel}${row.annualTonnes != null ? `\nAnnual reduction: ${row.annualTonnes.toLocaleString('en-GB', { maximumFractionDigits: 2 })} tCO2e` : ''}${isEditable ? '\nClick to edit' : ''}`
          return (
            <Tooltip key={row.id} title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} placement='top-start' followCursor>
              <Box
                onClick={() => editClickHandler(row.id)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  alignItems: 'stretch',
                  minHeight: 60,
                  borderBottom: '1px solid',
                  borderColor: 'lightGrey.main',
                  '&:last-of-type': { borderBottom: 'none' },
                  cursor: isEditable ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  '&:hover': isEditable ? { backgroundColor: 'action.hover' } : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, pl: 2, pr: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                  >
                    {row.name}
                  </Typography>
                  {isEditable && <EditOutlined sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />}
                </Box>
                <Box
                  sx={{
                    gridColumn: `${colStart} / ${colEnd}`,
                    alignSelf: 'center',
                    height: 24,
                    borderRadius: '6px',
                    bgcolor: color,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    ...(row.endYear == null
                      ? { background: `linear-gradient(90deg, ${color} 70%, ${color}33 100%)` }
                      : {}),
                  }}
                >
                  <Typography variant='caption' noWrap sx={{ color: '#fff', fontWeight: 600 }}>
                    {row.annualTonnes != null ? (
                      <NumericFormat value={row.annualTonnes} displayType='text' thousandSeparator=',' decimalScale={2} suffix=' tCO2e' />
                    ) : (row.endYear == null ? 'Ongoing' : '')}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          )
        })}
        </Box>
      </Paper>

      {/* Legend */}
      <Stack direction='row' flexWrap='wrap' gap={2} sx={{ mt: 1.5 }}>
        {(['Not Started', 'In Progress', 'Completed'] as const).map((status) => (
          <Stack key={status} direction='row' alignItems='center' gap={0.75}>
            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: STATUS_COLORS[status] }} />
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>{status}</Typography>
          </Stack>
        ))}
        {hasOngoing && (
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Faded right edge = ongoing (no end year).
          </Typography>
        )}
      </Stack>

      {openDialog && isEditable && (
        <ReductionActionPlanDialog
          actionType={actionType}
          open={openDialog}
          isSubmitting={isDialogSubmitting}
          reductionActionsForOrganisation={actions}
          getAllActionsForOrganisation={onActionUpdated}
          closeHandler={closeDialogHandler}
          submitHandler={submitDialogHandler}
          deleteHandler={deleteDialogHandler}
        />
      )}
    </Box>
  )
}
